import { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, Plus, Pencil, Trash2, Check, X, MessageCircle } from "lucide-react";

const CATS = ["fritti", "focacce", "signature", "classiche", "rosse", "cocktail", "dolci"];
const emptyItem = { category: "classiche", name_it: "", name_en: "", desc_it: "", desc_en: "", price: 0, image: "", available: true, order: 0 };

const STATUS_STYLE = {
  pending: "bg-[#FFF4E0] text-[#8a5a00]",
  confirmed: "bg-[#E8F5E9] text-[#2e7d32]",
  cancelled: "bg-[#FDECEA] text-[#c62828]",
};
const STATUS_LABEL = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" };

const SITE_SECTIONS = [
  { key: "hero", label: "Foto principale (apertura sito)" },
  { key: "manifesto", label: "01 — Manifesto" },
  { key: "menu", label: "02 — Menù" },
  { key: "prenotazione", label: "03 — Prenotazione" },
];
const CAT_IMAGE_KEYS = [
  { key: "cat_all", label: "Tutti" },
  { key: "cat_fritti", label: "Fritti e sfizi" },
  { key: "cat_focacce", label: "Focacce" },
  { key: "cat_signature", label: "Signature" },
  { key: "cat_classiche", label: "Classiche" },
  { key: "cat_rosse", label: "Rosse d'autore" },
  { key: "cat_cocktail", label: "Cocktail" },
  { key: "cat_dolci", label: "Dolci" },
];

function ImageCard({ imgKey, label, current, onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/site-image/${imgKey}`, fd);
      onUploaded(imgKey, data.url);
      setFile(null);
      toast.success("Immagine aggiornata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`site-image-${imgKey}`} className="bg-white border border-[#E5E5E5] p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#555] mb-3">{label}</p>
      {current && <img src={current.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${current}` : current} alt={label} className="w-full h-32 object-cover mb-3" loading="lazy" />}
      <input type="file" accept="image/*" data-testid={`site-image-input-${imgKey}`} onChange={(e) => setFile(e.target.files[0] || null)}
        className="text-xs file:mr-2 file:border file:border-[#E5E5E5] file:bg-white file:px-3 file:py-1.5 file:cursor-pointer mb-3 w-full" />
      <button data-testid={`site-image-save-${imgKey}`} onClick={upload} disabled={!file || busy}
        className="w-full text-[10px] uppercase tracking-[0.25em] bg-[#0A0A0A] text-[#FAF9F6] py-2.5 hover:bg-[#6B1E33] transition-colors disabled:opacity-40">
        {busy ? "…" : "Cambia immagine"}
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState("reservations");
  const [reservations, setReservations] = useState([]);
  const [menu, setMenu] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoAlt, setPhotoAlt] = useState("");
  const [photoSpan, setPhotoSpan] = useState("normal");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoSaving, setPhotoSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data.role === "admin" ? r.data : null)).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!me) return;
    api.get("/admin/reservations").then((r) => setReservations(r.data)).catch(() => {});
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/site-images").then((r) => setSiteImages(r.data)).catch(() => {});
  }, [me]);

  if (me === undefined) return <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center text-sm text-[#555]">Caricamento…</div>;
  if (me === null) return <Navigate to="/admin" replace />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/reservations/${id}`, { status });
      setReservations((rs) => rs.map((r) => (r.id === id ? data : r)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeReservation = async (id) => {
    try {
      await api.delete(`/admin/reservations/${id}`);
      setReservations((rs) => rs.filter((r) => r.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, price: Number(editing.price), order: Number(editing.order) || 0 };
      if (editing.id) {
        const { data } = await api.put(`/admin/menu/${editing.id}`, payload);
        setMenu((ms) => ms.map((m) => (m.id === editing.id ? data : m)));
      } else {
        const { data } = await api.post("/admin/menu", payload);
        setMenu((ms) => [...ms, data]);
      }
      setEditing(null);
      toast.success("Salvato");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/admin/menu/${id}`);
      setMenu((ms) => ms.filter((m) => m.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAvailable = async (item) => {
    try {
      const { data } = await api.put(`/admin/menu/${item.id}`, { ...item, available: !item.available });
      setMenu((ms) => ms.map((m) => (m.id === item.id ? data : m)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const waConfirmLink = (r) => {
    const raw = (r.phone || "").trim();
    let p;
    if (raw.startsWith("+")) {
      p = raw.replace(/[^0-9]/g, "");
    } else if (raw.replace(/[^0-9]/g, "").startsWith("00")) {
      p = raw.replace(/[^0-9]/g, "").slice(2);
    } else {
      p = raw.replace(/[^0-9]/g, "");
      if (p.startsWith("39")) p = p.slice(2);
      p = "39" + p.replace(/^0+/, "");
    }
    const msg = encodeURIComponent(`Ciao ${r.name}! La tua prenotazione da Funghetto Roma per ${r.guests} ospiti il ${r.date} alle ${r.time} è confermata. Ti aspettiamo in Via Urbano II 32, Roma. A presto!`);
    return `https://wa.me/${p}?text=${msg}`;
  };

  const setField = (k) => (e) => setEditing({ ...editing, [k]: e.target ? e.target.value : e });

  const refreshPhotos = () => api.get("/gallery").then((r) => setPhotos(r.data)).catch(() => {});

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setPhotoSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      fd.append("alt", photoAlt);
      fd.append("span", photoSpan);
      await api.post("/admin/gallery/upload", fd);
      setPhotoFile(null);
      setPhotoAlt("");
      refreshPhotos();
      toast.success("Foto caricata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPhotoSaving(false); }
  };

  const addPhotoUrl = async () => {
    if (!photoUrl.trim()) return;
    setPhotoSaving(true);
    try {
      await api.post("/admin/gallery", { url: photoUrl.trim(), alt: photoAlt, span: photoSpan });
      setPhotoUrl("");
      setPhotoAlt("");
      refreshPhotos();
      toast.success("Foto aggiunta");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPhotoSaving(false); }
  };

  const updatePhoto = async (id, patch) => {
    try {
      await api.put(`/admin/gallery/${id}`, patch);
      setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletePhoto = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      setPhotos((ps) => ps.filter((p) => p.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo" className="flex items-center gap-3"><img src="/logo.png" alt="Funghetto Roma" className="h-7 w-auto" /><span className="text-[10px] uppercase tracking-[0.3em] text-[#555]">Admin</span></Link>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#555] hover:text-[#6B1E33] transition-colors">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-8 border-b border-[#E5E5E5] mb-10">
          {[["reservations", `Prenotazioni (${reservations.length})`], ["menu", `Menu (${menu.length})`], ["gallery", `Galleria (${photos.length})`], ["images", "Immagini"]].map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`pb-4 text-xs uppercase tracking-[0.25em] transition-colors ${tab === key ? "text-[#6B1E33] font-bold border-b-2 border-[#6B1E33]" : "text-[#555] hover:text-[#0A0A0A]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "reservations" && (
          <div data-testid="admin-reservations-list" className="space-y-4">
            {reservations.length === 0 && <p className="text-sm text-[#555] py-10">Nessuna prenotazione.</p>}
            {reservations.map((r) => (
              <div key={r.id} data-testid={`admin-reservation-${r.id}`} className="bg-white border border-[#E5E5E5] p-5 flex flex-wrap items-center gap-x-8 gap-y-3">
                <div className="min-w-[160px]">
                  <p className="font-serif text-lg">{r.name}</p>
                  <p className="text-xs text-[#777]">{r.email} · {r.phone}</p>
                </div>
                <div className="text-sm">
                  <p className="font-medium">{r.date} · {r.time}</p>
                  <p className="text-xs text-[#777]">{r.guests} ospiti{r.note ? ` · ${r.note}` : ""}</p>
                </div>
                <span data-testid={`reservation-status-${r.id}`} className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 ${STATUS_STYLE[r.status]}`}>
                  {STATUS_LABEL[r.status]}
                </span>
                <div className="ml-auto flex gap-2">
                  <a data-testid={`reservation-whatsapp-${r.id}`} href={waConfirmLink(r)} target="_blank" rel="noopener noreferrer" title="Conferma su WhatsApp"
                    className="p-2 border border-[#E5E5E5] hover:bg-[#E8F5E9] transition-colors text-[#25D366]"><MessageCircle size={15} /></a>
                  {r.status !== "confirmed" && (
                    <button data-testid={`reservation-confirm-${r.id}`} onClick={() => setStatus(r.id, "confirmed")} title="Conferma"
                      className="p-2 border border-[#E5E5E5] hover:bg-[#E8F5E9] transition-colors"><Check size={15} /></button>
                  )}
                  {r.status !== "cancelled" && (
                    <button data-testid={`reservation-cancel-${r.id}`} onClick={() => setStatus(r.id, "cancelled")} title="Annulla"
                      className="p-2 border border-[#E5E5E5] hover:bg-[#FDECEA] transition-colors"><X size={15} /></button>
                  )}
                  <button data-testid={`reservation-delete-${r.id}`} onClick={() => removeReservation(r.id)} title="Elimina"
                    className="p-2 border border-[#E5E5E5] hover:bg-[#FDECEA] transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "menu" && (
          <div data-testid="admin-menu-list">
            <button data-testid="menu-add-btn" onClick={() => setEditing({ ...emptyItem })}
              className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.25em] bg-[#0A0A0A] text-[#FAF9F6] px-6 py-3.5 hover:bg-[#6B1E33] transition-colors">
              <Plus size={14} /> Aggiungi piatto
            </button>
            <div className="space-y-3">
              {menu.map((m) => (
                <div key={m.id} data-testid={`admin-menu-item-${m.id}`} className="bg-white border border-[#E5E5E5] p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#999] w-20">{m.category}</span>
                  <div className="min-w-[200px] flex-1">
                    <p className="font-serif">{m.name_it} <span className="text-[#999] font-sans text-xs">/ {m.name_en}</span></p>
                  </div>
                  <span className="font-serif italic">€ {m.price.toFixed(2).replace(".", ",")}</span>
                  <button data-testid={`menu-toggle-${m.id}`} onClick={() => toggleAvailable(m)}
                    className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${m.available ? "border-[#2e7d32] text-[#2e7d32]" : "border-[#c62828] text-[#c62828]"}`}>
                    {m.available ? "Disponibile" : "Esaurito"}
                  </button>
                  <div className="flex gap-2">
                    <button data-testid={`menu-edit-${m.id}`} onClick={() => setEditing({ ...m })} className="p-2 border border-[#E5E5E5] hover:bg-[#F3EFE7] transition-colors"><Pencil size={14} /></button>
                    <button data-testid={`menu-delete-${m.id}`} onClick={() => removeItem(m.id)} className="p-2 border border-[#E5E5E5] hover:bg-[#FDECEA] transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="admin-gallery-list">
            <div className="bg-white border border-[#E5E5E5] p-5 mb-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#555] mb-4">Aggiungi foto</p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#777] mb-1.5">Carica dal dispositivo</label>
                  <input data-testid="gallery-file-input" type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                    className="text-sm file:mr-3 file:border file:border-[#E5E5E5] file:bg-white file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.15em] hover:file:bg-[#F3EFE7] file:cursor-pointer" />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#777] mb-1.5">Descrizione</label>
                  <Input data-testid="gallery-alt-input" value={photoAlt} onChange={(e) => setPhotoAlt(e.target.value)} placeholder="Es. La nostra margherita" className="rounded-none h-11" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#777] mb-1.5">Dimensione in griglia</label>
                  <select data-testid="gallery-span-select" value={photoSpan} onChange={(e) => setPhotoSpan(e.target.value)}
                    className="h-11 border border-[#D8D4CC] bg-white text-sm px-3 rounded-none">
                    <option value="normal">Normale</option>
                    <option value="wide">Larga</option>
                    <option value="tall">Alta</option>
                    <option value="big">Grande</option>
                  </select>
                </div>
                <button data-testid="gallery-upload-btn" onClick={uploadPhoto} disabled={!photoFile || photoSaving}
                  className="text-xs uppercase tracking-[0.25em] bg-[#0A0A0A] text-[#FAF9F6] px-6 py-3.5 hover:bg-[#6B1E33] transition-colors disabled:opacity-40">
                  {photoSaving ? "…" : "Carica"}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#F0EDE6]">
                <Input data-testid="gallery-url-input" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="oppure incolla un URL immagine (https://…)" className="rounded-none h-11 flex-1 min-w-[240px]" />
                <button data-testid="gallery-add-url-btn" onClick={addPhotoUrl} disabled={!photoUrl.trim() || photoSaving}
                  className="text-xs uppercase tracking-[0.25em] border border-[#0A0A0A] px-6 py-3.5 hover:bg-[#0A0A0A] hover:text-[#FAF9F6] transition-colors disabled:opacity-40">
                  Aggiungi URL
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {photos.map((p) => (
                <div key={p.id} data-testid={`admin-photo-${p.id}`} className="bg-white border border-[#E5E5E5] p-3">
                  <img src={p.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt={p.alt}
                    className="w-full h-32 object-cover mb-3" loading="lazy" />
                  <Input data-testid={`photo-alt-${p.id}`} defaultValue={p.alt} placeholder="Descrizione"
                    onBlur={(e) => e.target.value !== p.alt && updatePhoto(p.id, { alt: e.target.value })}
                    className="rounded-none h-9 text-xs mb-2" />
                  <div className="flex items-center gap-2">
                    <select data-testid={`photo-span-${p.id}`} value={p.span} onChange={(e) => updatePhoto(p.id, { span: e.target.value })}
                      className="flex-1 h-9 border border-[#D8D4CC] bg-white text-xs px-2 rounded-none">
                      <option value="normal">Normale</option>
                      <option value="wide">Larga</option>
                      <option value="tall">Alta</option>
                      <option value="big">Grande</option>
                    </select>
                    <button data-testid={`photo-delete-${p.id}`} onClick={() => deletePhoto(p.id)}
                      className="p-2 border border-[#E5E5E5] hover:bg-[#FDECEA] transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "images" && (
          <div data-testid="admin-images-tab">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#555] mb-4">Immagini delle sezioni (visibili su desktop)</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {SITE_SECTIONS.map((s) => (
                <ImageCard key={s.key} imgKey={s.key} label={s.label} current={siteImages[s.key]}
                  onUploaded={(k, url) => setSiteImages((si) => ({ ...si, [k]: url }))} />
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#555] mb-4">Foto delle categorie del menù</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {CAT_IMAGE_KEYS.map((s) => (
                <ImageCard key={s.key} imgKey={s.key} label={s.label} current={siteImages[s.key]}
                  onUploaded={(k, url) => setSiteImages((si) => ({ ...si, [k]: url }))} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="menu-edit-dialog" className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing?.id ? "Modifica piatto" : "Nuovo piatto"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-1.5">Categoria</label>
                <select data-testid="menu-field-category" value={editing.category} onChange={setField("category")}
                  className="w-full h-11 border border-[#D8D4CC] bg-white text-sm px-3 rounded-none">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {[["name_it", "Nome (IT)"], ["name_en", "Nome (EN)"], ["desc_it", "Descrizione (IT)"], ["desc_en", "Descrizione (EN)"]].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-1.5">{label}</label>
                  {k.startsWith("desc")
                    ? <Textarea data-testid={`menu-field-${k}`} value={editing[k]} onChange={setField(k)} className="rounded-none min-h-[60px]" />
                    : <Input data-testid={`menu-field-${k}`} value={editing[k]} onChange={setField(k)} className="rounded-none h-11" />}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-1.5">Prezzo (€)</label>
                  <Input data-testid="menu-field-price" type="number" step="0.5" min="0" value={editing.price} onChange={setField("price")} className="rounded-none h-11" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-1.5">Ordine</label>
                  <Input data-testid="menu-field-order" type="number" value={editing.order} onChange={setField("order")} className="rounded-none h-11" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#555] mb-1.5">URL immagine (opzionale)</label>
                <Input data-testid="menu-field-image" value={editing.image} onChange={setField("image")} className="rounded-none h-11" />
              </div>
              <label className="flex items-center gap-3 text-sm pt-1">
                <input data-testid="menu-field-available" type="checkbox" checked={editing.available}
                  onChange={(e) => setEditing({ ...editing, available: e.target.checked })} className="w-4 h-4 accent-[#6B1E33]" />
                Disponibile
              </label>
              <button data-testid="menu-save-btn" onClick={saveItem} disabled={saving || !editing.name_it || !editing.name_en}
                className="w-full text-xs uppercase tracking-[0.3em] bg-[#0A0A0A] text-[#FAF9F6] py-4 hover:bg-[#6B1E33] transition-colors disabled:opacity-50">
                {saving ? "…" : "Salva"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
